import { FC } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { theme } from 'store';
import { debounce } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import { Select, TOOLBAR_FIELD_GAP, toolbarFieldSx } from 'ui';
import { MultiselectSearchableField } from 'components/Multiselect';
import {
  ColumnDensityFilter,
  WorklistTableColumnSelectionFilter,
} from 'components/TablePreferences';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useFilters } from 'views/pages/Worklist/AllWorklist/components/Incomplete/Filters/hooks';

interface FiltersProps {
  isFiltersCardOpen: boolean;
  handleToggleFiltersCard: () => void;
}

export const Filters: FC<FiltersProps> = ({
  isFiltersCardOpen,
  handleToggleFiltersCard,
}) => {
  const {
    handleSearchChange,
    filters,
    handlePartnerOnChange,
    handleProviderOnChange,
    providerOptions,
    partnerOptions,
    appliedFiltersCount,
    handleUpdatePreferences,
    preferences,
  } = useFilters();

  return (
    <Box
      sx={{
        width: '100%',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        bgcolor: 'white',
        border: theme => `1px solid ${theme.palette.grey[300]}`,
        borderBottom: 'none',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: TOOLBAR_FIELD_GAP,
          width: '100%',
          boxSizing: 'border-box',
          alignItems: 'center',
          maxWidth: { lg: '600px', xl: '800px' },
        }}
      >
        <OutlinedInput
          id='searchField'
          placeholder='Search patient'
          defaultValue={filters.search}
          onChange={debounce(handleSearchChange, 500)}
          sx={toolbarFieldSx}
          startAdornment={
            <InputAdornment sx={{ padding: 0 }} position='start'>
              <SearchIcon />
            </InputAdornment>
          }
        />
        <Select
          name='partner'
          size='medium'
          label='Partner'
          onChange={handlePartnerOnChange}
          value={filters.partner_id}
          options={partnerOptions}
          clearLabel='All'
          clearable
          sx={{
            ...toolbarFieldSx,
            display: 'flex',
            justifyContent: 'center',
          }}
        />
        <MultiselectSearchableField
          options={providerOptions}
          value={
            Array.isArray(filters.provider_ids) ? filters?.provider_ids : []
          }
          name={'provider_ids'}
          label={'Provider'}
          handleChange={values => handleProviderOnChange(values)}
          optional
          hideHelperText
          sx={toolbarFieldSx}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <WorklistTableColumnSelectionFilter hideText={isFiltersCardOpen} />
        <ColumnDensityFilter
          handleDensityChange={density => {
            handleUpdatePreferences({ density });
          }}
          selectedValue={preferences?.density}
          hideText={isFiltersCardOpen}
        />
        <Button
          sx={{
            display: 'flex',
            gap: 0.5,
            fontWeight: 'medium',
            fontSize: '15px',
            ...(isFiltersCardOpen && {
              bgcolor: theme.palette.selectedTableRowBg.main,
            }),
          }}
          variant='text'
          onClick={handleToggleFiltersCard}
        >
          <FilterAltIcon />
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 'medium',
              display: {
                lg: 'initial',
                md: isFiltersCardOpen ? 'none' : 'initial',
                sm: 'none',
              },
            }}
          >
            FILTERS
          </Typography>
          <Typography sx={{ fontSize: '15px', fontWeight: 'medium' }}>
            {appliedFiltersCount > 0 ? `(${appliedFiltersCount})` : ''}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};
